import { forwardRef, useRef, useCallback, type CSSProperties, type ReactNode, type KeyboardEvent } from 'react';
import { SparkIcon } from '../SparkIcon';
import { TooltipTrigger } from '../Tooltip';
import { useControllableState } from '../../hooks/useControllableState';
import { mergeRefs } from '../../utils/mergeRefs';
import './SegmentedControl.css';

export type SegmentedControlSize = 'small' | 'medium' | 'large';
export type SegmentedControlStyle = 'outlined' | 'solid';
export type SegmentedControlItemPosition = 'first' | 'center' | 'last';
export type SegmentedControlItemVisualState = 'default' | 'hover' | 'disabled';

export type SegmentedControlItemData = {
  id: string;
  label: string;
  disabled?: boolean;
  icon?: ReactNode;
  /** Optional trailing icon (for example an info affordance after the label). */
  endIcon?: ReactNode;
  /** Hover tooltip shown over this segment. */
  tooltip?: string;
  /**
   * Rich hover tooltip for this segment (takes precedence over `tooltip`). The
   * bubble stays open while hovered so links inside it can be clicked — used to
   * explain a disabled segment and offer a way out.
   */
  tooltipContent?: ReactNode;
};

/** Single segment button inside a SegmentedControl. */
export type SegmentedControlItemProps = {
  label?: string;
  active?: boolean;
  size?: SegmentedControlSize;
  style?: SegmentedControlStyle;
  position?: SegmentedControlItemPosition;
  disabled?: boolean;
  showIcon?: boolean;
  icon?: ReactNode;
  endIcon?: ReactNode;
  visualState?: SegmentedControlItemVisualState;
  /**
   * Sibling stacking order (higher paints on top). Segments overlap by 1px, so
   * each item must stack above the next one for the shared divider border to
   * stay visible — including between two adjacent "center" items.
   */
  stackOrder?: number;
  onClick?: () => void;
  className?: string;
};

/** Horizontal group of mutually exclusive segment buttons. Supports outlined and solid styles. Controlled and uncontrolled. */
export type SegmentedControlProps = {
  items: SegmentedControlItemData[];
  /** Controlled active item id. */
  activeId?: string;
  /** Initial active item id for uncontrolled mode. */
  defaultActiveId?: string;
  onChange?: (id: string) => void;
  size?: SegmentedControlSize;
  style?: SegmentedControlStyle;
  fill?: boolean;
  showIcon?: boolean;
  className?: string;
};

function getItemPosition(index: number, total: number): SegmentedControlItemPosition {
  if (index === 0) {
    return 'first';
  }
  if (index === total - 1) {
    return 'last';
  }
  return 'center';
}

export function SegmentedControlItem({
  label = 'Text',
  active = false,
  size = 'medium',
  style = 'outlined',
  position = 'first',
  disabled = false,
  showIcon = false,
  icon,
  endIcon,
  visualState,
  stackOrder,
  onClick,
  className = '',
}: SegmentedControlItemProps) {
  const isDisabled = disabled || visualState === 'disabled';

  const itemClasses = [
    'spark-segmented-control-item',
    `spark-segmented-control-item-${position}`,
    active ? 'spark-segmented-control-item-active' : '',
    isDisabled ? 'spark-segmented-control-item-disabled' : '',
    visualState === 'hover' && !active && !isDisabled ? 'spark-segmented-control-item-state-hover' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const inlineStyle =
    stackOrder !== undefined
      ? ({ '--spark-segmented-control-item-z': stackOrder } as CSSProperties)
      : undefined;

  return (
    /*
     * Every segment is a Tab stop (native button tabIndex) rather than the
     * roving-tabindex tablist pattern: forms must be traversable with Tab
     * alone, so Tab iterates the options and Enter/Space (or arrows) selects.
     */
    <button
      type="button"
      role="tab"
      className={itemClasses}
      disabled={isDisabled}
      aria-selected={active}
      onClick={onClick}
      data-size={size}
      data-style={style}
      style={inlineStyle}
    >
      <span className="spark-segmented-control-item-content">
        {showIcon ? (icon ?? <SparkIcon slug="home" size="small" className="spark-segmented-control-item-icon" />) : null}
        <span className="spark-segmented-control-item-label">{label}</span>
        {endIcon ? <span className="spark-segmented-control-item-end-icon">{endIcon}</span> : null}
      </span>
    </button>
  );
}

export const SegmentedControl = forwardRef<HTMLDivElement, SegmentedControlProps>(function SegmentedControl({
  items,
  activeId: activeIdProp,
  defaultActiveId,
  onChange,
  size = 'medium',
  style = 'outlined',
  fill = false,
  showIcon = false,
  className = '',
}, ref) {
  const [activeId, setActiveId] = useControllableState(
    activeIdProp,
    defaultActiveId ?? items[0]?.id ?? '',
    onChange,
  );

  const containerRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();

      const enabledItems = items.filter((item) => !item.disabled);
      if (enabledItems.length === 0) return;

      const currentIndex = enabledItems.findIndex((item) => item.id === activeId);
      let nextIndex: number;

      if (e.key === 'ArrowRight') {
        nextIndex = currentIndex >= enabledItems.length - 1 ? 0 : currentIndex + 1;
      } else {
        nextIndex = currentIndex <= 0 ? enabledItems.length - 1 : currentIndex - 1;
      }

      const nextId = enabledItems[nextIndex].id;
      setActiveId(nextId);

      const buttons = containerRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
      const fullIndex = items.findIndex((item) => item.id === nextId);
      buttons?.[fullIndex]?.focus();
    },
    [items, activeId, setActiveId],
  );

  const rootClasses = [
    'spark-segmented-control',
    `spark-segmented-control-${size}`,
    `spark-segmented-control-${style}`,
    fill ? 'spark-segmented-control-fill' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClasses} role="tablist" ref={mergeRefs(ref, containerRef)} onKeyDown={handleKeyDown}>
      {items.map((item, index) => {
        const segment = (
          <SegmentedControlItem
            key={item.id}
            label={item.label}
            active={item.id === activeId}
            size={size}
            style={style}
            position={getItemPosition(index, items.length)}
            stackOrder={items.length - index}
            disabled={item.disabled}
            showIcon={showIcon || item.icon !== undefined}
            icon={item.icon}
            endIcon={item.endIcon}
            onClick={() => setActiveId(item.id)}
          />
        );
        if (item.tooltipContent !== undefined && item.tooltipContent !== null) {
          return (
            <TooltipTrigger key={item.id} content={item.tooltipContent} interactive>
              {segment}
            </TooltipTrigger>
          );
        }
        return item.tooltip ? (
          <TooltipTrigger key={item.id} text={item.tooltip}>
            {segment}
          </TooltipTrigger>
        ) : (
          segment
        );
      })}
    </div>
  );
});

SegmentedControl.displayName = 'SegmentedControl';
